// Transaction Wizard — the step machine (docs/plans/transaction-wizard.md §8,
// §10.3, §10.6): frames and the call stack, the cursor, Back snapshots and the
// boundaries Back may not cross, next / cases / when, `repeat`, loop bounds,
// call → return routing by outcome, and error → step mapping.
//
// Pure and synchronous: every function takes a frame (or a run) and returns a
// new one. What a step DOES against the server is ops.ts; what it LOOKS like is
// the step components. That split is what lets test_wizard_runner.mjs drive a
// whole wizard without a browser.

import { columnDbName, columnFormKey } from '../txn/utils'
import type {
  ValidateTreeIssue, WizardCallBlock, WizardCallOutcome, WizardFillResult, WizardFrame, WizardInput,
  WizardModel, WizardOp, WizardReturnRoute, WizardRun, WizardSnapshot, WizardStep,
  WizardTrailEntry,
} from './contract'
import {
  DEFAULT_LIMITS, columnOf, evalCondition, headingOf, isBlank, isInteractive, keyOf,
  liveRows, nextRoute, parseTarget, readCol, render, routeOf, routeTargets, scopeOf,
  stepById, type FormIndex, type Route, type Row, type Target, type WizardScope,
} from './model'

/** Snapshots kept per frame (the record is small; a large grid is not). */
export const MAX_BACK = 40
/** Trail entries kept per frame. */
export const MAX_TRAIL = 200
/** Synthetic first step that asks for required inputs nobody supplied. */
export const START_STEP = '__start'

export class WizardError extends Error {
  code: string
  constructor(code: string, message: string) {
    super(message)
    this.name = 'WizardError'
    this.code = code
  }
}

// ── Frames ───────────────────────────────────────────────────────────────

export function newRunId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto
  if (c?.randomUUID) return c.randomUUID()
  return 'wr-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10)
}

/** A fresh frame: vars and inputs at their defaults, an empty record. The
 *  record itself (defaults / locate) is filled by ops.initRecord. */
export function newFrame(
  model: WizardModel, fi: FormIndex,
  opts: { inputs?: Record<string, unknown>; locked?: string[] } = {},
): WizardFrame {
  const vars: Record<string, unknown> = {}
  for (const v of model.vars || []) vars[v.name] = v.default ?? null
  const input: Record<string, unknown> = {}
  for (const i of model.inputs || []) {
    if (i.default !== undefined) input[i.name] = i.default
  }
  for (const [k, v] of Object.entries(opts.inputs || {})) {
    if (v !== undefined) input[k] = v
  }
  return {
    wizard: model.obj_name,
    fingerprint: model.fingerprint,
    cursor: '',
    mode: model.mode,
    txn_id: null,
    record: { header: {}, details: {} },
    vars,
    input,
    line_index: null,
    line_form: model.line_form || fi.lineForm,
    history: [],
    repeat: {},
    save_status: 'none',
    locked: opts.locked || [],
    steps: {},
    back_stack: [],
    visits: {},
    dirty: false,
    protect: {},
    trail: [],
  }
}

/** Required inputs the launch did not supply (asked for in a Start step). */
export function missingInputs(model: WizardModel, input: Record<string, unknown>): WizardInput[] {
  return (model.inputs || []).filter(i => i.required && isBlank(input[i.name]))
}

export function trail(frame: WizardFrame, kind: WizardTrailEntry['kind'], step?: string, detail?: string): WizardFrame {
  const t = [...(frame.trail || []), { at: new Date().toISOString(), kind, step, detail }]
  return { ...frame, trail: t.length > MAX_TRAIL ? t.slice(t.length - MAX_TRAIL) : t }
}

// ── Back ─────────────────────────────────────────────────────────────────

export function snapshotOf(frame: WizardFrame): WizardSnapshot {
  return {
    cursor: frame.cursor,
    record: frame.record,
    vars: frame.vars,
    steps: frame.steps,
    line_index: frame.line_index ?? null,
    line_form: frame.line_form,
    repeat: frame.repeat,
    txn_id: frame.txn_id ?? null,
    save_status: frame.save_status,
    dirty: frame.dirty,
    protect: frame.protect,
  }
}

/** Put the cursor on an INTERACTIVE step and remember the frame as it is now
 *  (an undo point). Values are immutable, so a snapshot is a set of
 *  references, not a copy. */
export function enterStep(frame: WizardFrame, stepId: string): WizardFrame {
  const entered = { ...frame, cursor: stepId }
  const stack = [...(frame.back_stack || []), snapshotOf(entered)]
  return {
    ...entered,
    back_stack: stack.length > MAX_BACK ? stack.slice(stack.length - MAX_BACK) : stack,
  }
}

/** The user is leaving the step on screen with what they entered: remember
 *  it, so Back returns to the step showing their input — while everything the
 *  step's `then` and the steps after it did is undone (re-running them on Next
 *  must not, say, add a line twice). Not for a step whose input IS its effect
 *  (a scan, a multi-select adding lines): Back there restores the entry. */
export function markLeft(frame: WizardFrame): WizardFrame {
  const stack = frame.back_stack || []
  if (!stack.length || stack[stack.length - 1].cursor !== frame.cursor) return frame
  const last = stack[stack.length - 1]
  return { ...frame, back_stack: [...stack.slice(0, -1), { ...last, left: snapshotOf(frame) }] }
}

/** Record that the machine passed through a step (any kind). Refuses the
 *  visit that would exceed `max_loop_iterations` — a loop without an exit is
 *  stopped rather than spun. */
export function visit(frame: WizardFrame, model: WizardModel, stepId: string): WizardFrame {
  const max = model.limits?.max_loop_iterations || DEFAULT_LIMITS.max_loop_iterations
  const n = (frame.visits?.[stepId] || 0) + 1
  if (n > max) {
    throw new WizardError('WIZARD_LOOP_LIMIT',
      `Step "${stepId}" ran ${max} times — the wizard stopped to avoid an endless loop.`)
  }
  const history = frame.history[frame.history.length - 1] === stepId
    ? frame.history : [...frame.history, stepId]
  return { ...frame, visits: { ...(frame.visits || {}), [stepId]: n }, history }
}

/** Whether Back is possible, and why not. */
export function canBack(model: WizardModel, frame: WizardFrame): { ok: boolean; reason?: string } {
  const stack = frame.back_stack || []
  if (stack.length < 2) {
    return { ok: false, reason: frame.save_status !== 'none' || (frame.trail || []).some(t => t.kind === 'print' || t.kind === 'call')
      ? 'Saved, printed or handed over — that cannot be undone from here.' : undefined }
  }
  const prev = stack[stack.length - 2]
  const step = stepById(model, prev.cursor)
  if (step && step.back === false) {
    return { ok: false, reason: `You cannot go back to "${step.title || step.id}".` }
  }
  return { ok: true }
}

/** Undo to the previous interactive step, as it was when it was entered. */
export function back(model: WizardModel, frame: WizardFrame): WizardFrame | null {
  if (!canBack(model, frame).ok) return null
  const stack = (frame.back_stack || []).slice(0, -1)
  const entry = stack[stack.length - 1]
  const snap = entry.left || entry
  // Back on this step again starts from its entry; leaving re-marks it.
  stack[stack.length - 1] = { ...entry, left: undefined }
  return {
    ...frame,
    cursor: snap.cursor,
    record: snap.record,
    vars: snap.vars,
    steps: snap.steps,
    line_index: snap.line_index ?? null,
    line_form: snap.line_form,
    repeat: snap.repeat,
    txn_id: snap.txn_id ?? null,
    save_status: snap.save_status,
    dirty: snap.dirty,
    protect: snap.protect,
    back_stack: stack,
    history: frame.history.length ? [...frame.history, snap.cursor] : [snap.cursor],
  }
}

/** A server effect that cannot be undone happened (a save, a print, a
 *  child's commit): Back may not cross it. */
export function boundary(frame: WizardFrame): WizardFrame {
  return { ...frame, back_stack: [] }
}

// ── Routing ──────────────────────────────────────────────────────────────

/** The innermost active repeat whose `until_step` is `stepId`. */
export function repeatEndingAt(model: WizardModel, frame: WizardFrame, stepId: string): string | null {
  const active = Object.keys(frame.repeat || {})
  for (let i = active.length - 1; i >= 0; i--) {
    const r = stepById(model, active[i])
    if (r && r.until_step === stepId) return r.id
  }
  return null
}

/** Where the flow goes after `step` was done: back to the repeat it ends an
 *  iteration of, else its own `next`. Null ⇒ the wizard finishes. */
export function routeAfter(model: WizardModel, frame: WizardFrame, step: WizardStep, scope: WizardScope): Route | null {
  const rep = repeatEndingAt(model, frame, step.id)
  if (rep) return { goto: rep }
  return nextRoute(step, scope)
}

function sortValue(v: unknown): number | string {
  if (typeof v === 'number') return v
  const s = String(v ?? '').trim()
  return s !== '' && !Number.isNaN(Number(s)) ? Number(s) : s.toLowerCase()
}

/** Enter (or re-enter) a `repeat`: the first entry builds the queue of lines
 *  matching `over` in `order_by`; each re-entry moves to the next line still
 *  matching. Returns the frame (current line set) and where to go. */
export function repeatStep(
  model: WizardModel, fi: FormIndex, frame: WizardFrame, step: WizardStep,
  scopeFor: (f: WizardFrame) => WizardScope,
): { frame: WizardFrame; route: Route | null } {
  const form = String(step.form_no || model.line_form || fi.lineForm)
  const rows = frame.record.details[form] || []
  const matches = (idx: number): boolean => {
    const row = rows[idx]
    if (!row || row._deleted) return false
    if (!step.over) return true
    const probe = { ...frame, line_form: form, line_index: idx }
    return evalCondition(step.over, scopeFor(probe), false)
  }
  const prior = frame.repeat?.[step.id]
  let queue: number[]
  let pos: number
  if (!prior) {
    queue = rows.map((_, i) => i).filter(matches)
    const order = step.order_by || []
    if (order.length) {
      queue.sort((a, b) => {
        for (const col of order) {
          const x = sortValue(readCol(rows[a], fi, form, col))
          const y = sortValue(readCol(rows[b], fi, form, col))
          if (x < y) return -1
          if (x > y) return 1
        }
        return a - b
      })
    }
    pos = 0
  } else {
    queue = prior.queue
    pos = prior.pos + 1
  }
  // A line an earlier iteration already finished no longer matches — skip it.
  while (pos < queue.length && !matches(queue[pos])) pos += 1
  if (pos < queue.length && step.body) {
    return {
      frame: {
        ...frame,
        repeat: { ...(frame.repeat || {}), [step.id]: { queue, pos } },
        line_form: form,
        line_index: queue[pos],
      },
      route: { goto: step.body },
    }
  }
  const repeat = { ...(frame.repeat || {}) }
  delete repeat[step.id]
  const done = { ...frame, repeat }
  return { frame: done, route: nextRoute(step, scopeFor(done)) }
}

/** "Line 3 of 12" for the innermost active repeat. */
export function repeatProgress(frame: WizardFrame): { index: number; total: number } | null {
  const ids = Object.keys(frame.repeat || {})
  if (!ids.length) return null
  const r = frame.repeat![ids[ids.length - 1]]
  return { index: r.pos + 1, total: r.queue.length }
}

/** Steps between `from` and a finish along the shortest path (interactive
 *  steps only) — "about N steps left". Branching makes a fixed count a lie,
 *  so this is an estimate and is labelled one. */
export function remainingEstimate(model: WizardModel, from: string): number {
  const dist = new Map<string, number>()
  const start = stepById(model, from)
  if (!start) return 0
  // Dijkstra over 0/1 weights: an interactive step costs 1.
  const queue: Array<[string, number]> = [[from, 0]]
  let best = Infinity
  while (queue.length) {
    queue.sort((a, b) => a[1] - b[1])
    const [id, d] = queue.shift()!
    if (dist.has(id)) continue
    dist.set(id, d)
    const step = stepById(model, id)
    if (!step) { best = Math.min(best, d); continue }
    const cost = (s: WizardStep) => (isInteractive(s.kind) ? 1 : 0)
    const targets = routeTargets(step).filter(t => stepById(model, t))
    if (!targets.length) { best = Math.min(best, d); continue }
    for (const t of targets) {
      const ts = stepById(model, t)!
      if (!dist.has(t)) queue.push([t, d + cost(ts)])
    }
  }
  return Number.isFinite(best) ? best : 0
}

// ── Finish and the call stack ────────────────────────────────────────────

export function finishOutcome(frame: WizardFrame): WizardCallOutcome {
  return frame.save_status === 'pending_approval' ? 'pending_approval' : 'completed'
}

/** A `fill` wizard's result (docs/plans/wizard-external-website-step.md §7):
 *  the header values that differ from what the opening screen handed over,
 *  and the rows the run ADDED to each child form (rows that came from the
 *  screen carry `_wz_seed`; deleted rows are dropped; `_wz_*` bookkeeping is
 *  stripped). Rows of the screen the run changed are not handed back. */
export function fillResult(frame: WizardFrame, headerNo = '1'): WizardFillResult {
  const seed = frame.fill_seed || {}
  const header: Record<string, unknown> = {}
  const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null)
  for (const [k, v] of Object.entries(frame.record.header || {})) {
    if (k.startsWith('_')) continue
    if (!same(v, seed[k])) header[k] = v
  }
  const lines: Record<string, Array<Record<string, unknown>>> = {}
  for (const [fno, rows] of Object.entries(frame.record.details || {})) {
    if (fno === headerNo) continue
    const added = (rows || []).filter(r => !r._wz_seed && !r._deleted).map((r) => {
      const out: Record<string, unknown> = {}
      for (const [k, v] of Object.entries(r)) if (!k.startsWith('_wz_')) out[k] = v
      return out
    })
    if (added.length) lines[fno] = added
  }
  return { header, lines }
}

/** `outputs` templates, evaluated when the wizard finishes. */
export function computeOutputs(model: WizardModel, scope: WizardScope): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [k, tpl] of Object.entries(model.outputs || {})) out[k] = render(tpl, scope)
  return out
}

/** The call block of a step / tool / on_issue entry. */
export function callBlockOf(step: WizardStep): WizardCallBlock {
  return {
    wizard: String(step.wizard || ''),
    ask: step.ask, with: step.with, lock_inputs: step.lock_inputs, run_as: step.run_as,
    returns: step.returns, on_return: step.on_return,
  }
}

/** Refuses a call the stack cannot take (depth, or a wizard already on it). */
export function checkCall(run: WizardRun, model: WizardModel, child: string): void {
  const depth = model.limits?.max_call_depth || DEFAULT_LIMITS.max_call_depth
  // The root is not a call: a stack of N frames is N-1 calls deep.
  if (run.stack.length > depth) {
    throw new WizardError('WIZARD_CALL_DEPTH', `Wizards can call each other at most ${depth} deep.`)
  }
  if (run.stack.some(f => f.wizard === child)) {
    throw new WizardError('WIZARD_CALL_CYCLE', `"${child}" is already open in this run.`)
  }
}

/** Push a child frame; the parent waits on `pending`. */
export function pushCall(run: WizardRun, child: WizardFrame, pending: NonNullable<WizardFrame['pending_call']>): WizardRun {
  const stack = run.stack.slice()
  const top = stack.length - 1
  stack[top] = { ...stack[top], pending_call: pending }
  stack.push(child)
  return { ...run, stack }
}

/** Pop the top frame; its parent records the outcome and outputs. */
export function popCall(run: WizardRun, outcome: WizardCallOutcome, outputs: Record<string, unknown>): WizardRun {
  if (run.stack.length < 2) return run
  const stack = run.stack.slice(0, -1)
  const top = stack.length - 1
  const pc = stack[top].pending_call
  stack[top] = { ...stack[top], pending_call: { ...(pc || { step_id: '' }), outcome, outputs } }
  return { ...run, stack }
}

/** What the caller does with a returned outcome: `on_return[outcome]`, else
 *  the default for that outcome (completed → next, cancelled / failed → back
 *  where the user was, pending_* → suspend). */
export function returnRoute(call: WizardCallBlock, outcome: WizardCallOutcome): WizardReturnRoute & { resume: string } {
  const r = call.on_return?.[outcome]
  if (r) return { ...r, resume: r.resume || 'same' }
  if (outcome === 'completed') return { resume: 'next' }
  if (outcome === 'pending_approval' || outcome === 'pending_assignee') return { suspend: true, resume: 'same' }
  if (outcome === 'failed') return { resume: 'same', message: 'The other wizard could not finish.' }
  return { resume: 'same' }
}

/** The route `resume` names: a step id, 'same' or 'next'. */
export function resumeTarget(resume: string, pending: NonNullable<WizardFrame['pending_call']>): Route | null {
  if (resume === 'same') return pending.resume_same ? { goto: pending.resume_same } : null
  if (resume === 'next') {
    if (pending.resume_next === null) return { finish: true }
    return pending.resume_next ? routeOf(pending.resume_next) : null
  }
  return routeOf(resume)
}

export function isFinalOutcome(o: WizardCallOutcome | undefined): boolean {
  return o === 'completed' || o === 'cancelled' || o === 'failed'
}

/** The server has recorded the outcome the parent was waiting on (an approval
 *  decided, an assignee handed back): the top frame's `on_return` can run. */
export function readyToReturn(run: WizardRun): boolean {
  const top = run.stack[run.stack.length - 1]
  return !!top?.pending_call && isFinalOutcome(top.pending_call.outcome)
}

/** What a suspended caller waits on: the approval of a staged child save
 *  (`ref` = the approval INSTANCE id, which the server's decision hook keys
 *  on), or someone else completing the child. */
export function waitingOnFor(outcome: WizardCallOutcome, outputs: Record<string, unknown>): NonNullable<WizardRun['waiting_on']> {
  if (outcome === 'pending_assignee') return { type: 'assignee' }
  const ref = outputs._approval_ref
  return { type: 'approval', ...(ref != null && String(ref) !== '' ? { ref: String(ref) } : {}) }
}

/** The owner's run while a `run_as: {role}` child waits for the role. */
export function assigneeWaitingOn(block: WizardCallBlock, title?: string): NonNullable<WizardRun['waiting_on']> {
  const role = block.run_as && typeof block.run_as === 'object' ? block.run_as.role : ''
  return { type: 'assignee', role, wizard: block.wizard, ...(title ? { title } : {}) }
}

/** An assignee hands the child back: a staged save still counts as done by
 *  them (the approval is the requester's to wait for). */
export function handbackOutcome(o: WizardCallOutcome): 'completed' | 'cancelled' | 'failed' {
  if (o === 'cancelled' || o === 'failed') return o
  return 'completed'
}

/** `returns` — caller target ← child output — as `set` operations. */
export function returnSets(call: WizardCallBlock, outputs: Record<string, unknown>): WizardOp[] {
  return Object.entries(call.returns || {}).map(([target, output]) => ({
    op: 'set', target, value: outputs[output] ?? null,
  }))
}

// ── Errors → steps (§10.6) ───────────────────────────────────────────────

/** Every target a step captures (input fields, select target, scan map, grid
 *  editable, set operations). */
export function capturedTargets(model: WizardModel, step: WizardStep, fi: FormIndex): Target[] {
  const out: Target[] = []
  const form = String(step.form_no || model.line_form || fi.lineForm)
  const add = (t: string | undefined, dflt?: string) => {
    const p = t ? parseTarget(t, fi, dflt ?? form) : null
    if (p) out.push(p)
  }
  for (const f of step.fields || []) add(typeof f === 'string' ? f : f.field)
  add(step.target)
  for (const k of Object.keys(step.map || {})) add(k)
  for (const k of step.editable || []) add(k, form)
  for (const op of [...(step.then || []), ...(step.do || [])]) {
    if (op.op === 'set') add(op.target)
  }
  return out
}

function sameColumn(fi: FormIndex, formNo: string, a: string, b: string): boolean {
  if (!a || !b) return false
  if (a.toLowerCase() === b.toLowerCase()) return true
  const col = columnOf(fi, formNo, a)
  if (!col) return keyOf(fi, formNo, b) === a.toLowerCase()
  const other = columnOf(fi, formNo, b)
  if (other) return columnFormKey(col) === columnFormKey(other)
  return columnDbName(col) === String(b).toLowerCase()
}

/** The steps that capture `field` on `formNo` — interactive ones first. */
export function stepsForField(model: WizardModel, fi: FormIndex, field: string, formNo: string): string[] {
  const hits: WizardStep[] = []
  for (const step of model.steps) {
    const caps = capturedTargets(model, step, fi)
    if (caps.some(t => t.scope !== 'var' && t.formNo === formNo && sameColumn(fi, formNo, t.name, field))) {
      hits.push(step)
    }
  }
  hits.sort((a, b) => Number(isInteractive(b.kind)) - Number(isInteractive(a.kind)))
  return hits.map(s => s.id)
}

export interface StepIssue extends ValidateTreeIssue {
  /** Steps that capture the field, best first (empty ⇒ a record error). */
  steps: string[]
  /** The column's heading (or the field as given). */
  label: string
}

/** Split `line[3].batch_no` / `2.batch_no` / `batch_no` into its parts. */
export function parseIssueField(field: string | null | undefined): { field: string; form?: string; row?: number } {
  const f = String(field || '').trim()
  const m = /^(?:line|lines|form)?(\d*)\[(\d+)\]\.(.+)$/.exec(f)
  if (m) return { field: m[3], form: m[1] || undefined, row: Number(m[2]) }
  const n = /^(\d+)\.(.+)$/.exec(f)
  if (n) return { field: n[2], form: n[1] }
  return { field: f }
}

/** Map engine issues onto the steps that capture their columns. */
export function mapIssues(model: WizardModel, fi: FormIndex, issues: ValidateTreeIssue[]): StepIssue[] {
  return issues.map((iss) => {
    const parsed = parseIssueField(iss.field)
    const formNo = String(iss.form_no || parsed.form || (parsed.row != null || iss.row != null ? (model.line_form || fi.lineForm) : fi.headerNo))
    const row = iss.row ?? parsed.row ?? null
    const steps = parsed.field ? stepsForField(model, fi, parsed.field, formNo) : []
    return {
      ...iss,
      form_no: formNo,
      row,
      field: parsed.field || iss.field || null,
      steps,
      label: parsed.field ? headingOf(fi, formNo, parsed.field) : '',
    }
  })
}

/** The step a field-less `on_issue` route names for an issue code. */
export function issueRoute(step: WizardStep | null, code: string | null | undefined):
  { goto?: string; message?: string; call?: WizardCallBlock } | null {
  if (!step || !code) return null
  const c = String(code).trim().toUpperCase()
  return (step.on_issue || []).find(r => String(r.code).trim().toUpperCase() === c) || null
}

// ── Required fields of a step ────────────────────────────────────────────

/** Blank required fields of an input step: the model's `mandatory`, the
 *  field's own `required` (never weaker than mandatory) and `required_when`.
 *  Returns target string → message. */
export function missingRequired(
  model: WizardModel, fi: FormIndex, step: WizardStep, frame: WizardFrame, scope: WizardScope,
): Record<string, string> {
  const out: Record<string, string> = {}
  const form = String(step.form_no || model.line_form || fi.lineForm)
  for (const f of step.fields || []) {
    const spec = typeof f === 'string' ? { field: f } as { field: string; required?: boolean } : f
    const t = parseTarget(spec.field, fi, form)
    if (!t) continue
    const col = t.scope === 'var' ? null : columnOf(fi, t.formNo, t.name)
    const mandatory = !!col && (col.mandatory === true || String(col.mandatory ?? '').toLowerCase() === 'true')
    const when = step.required_when?.[spec.field] ?? step.required_when?.[t.name]
    const required = mandatory || spec.required === true || (when ? evalCondition(when, scope, false) : false)
    if (!required) continue
    const v = t.scope === 'var' ? frame.vars[t.name]
      : t.scope === 'header' ? readCol(frame.record.header, fi, t.formNo, t.name)
        : readCol(scope.line, fi, t.formNo, t.name)
    if (isBlank(v)) out[spec.field] = `${t.scope === 'var' ? t.name : headingOf(fi, t.formNo, t.name)} is required`
  }
  return out
}

/** A grid's `required_when` failures: row index → column → message. */
export function gridRequired(
  model: WizardModel, fi: FormIndex, step: WizardStep, frame: WizardFrame,
  scopeFor: (f: WizardFrame) => WizardScope,
): Record<number, Record<string, string>> {
  const out: Record<number, Record<string, string>> = {}
  const form = String(step.form_no || model.line_form || fi.lineForm)
  const rows = frame.record.details[form] || []
  rows.forEach((row, idx) => {
    if (row._deleted) return
    const probe = { ...frame, line_form: form, line_index: idx }
    const scope = scopeFor(probe)
    if (step.row_filter && !evalCondition(step.row_filter, scope, true)) return
    for (const [col, when] of Object.entries(step.required_when || {})) {
      if (!evalCondition(when, scope, false)) continue
      if (isBlank(readCol(row, fi, form, col))) {
        out[idx] = { ...(out[idx] || {}), [col]: `${headingOf(fi, form, col)} is required` }
      }
    }
  })
  return out
}

/** Lines a grid shows: index into the form's rows, filtered by `row_filter`. */
export function gridRows(
  model: WizardModel, fi: FormIndex, step: WizardStep, frame: WizardFrame,
  scopeFor: (f: WizardFrame) => WizardScope,
): number[] {
  const form = String(step.form_no || model.line_form || fi.lineForm)
  const rows = frame.record.details[form] || []
  return rows.map((_, i) => i).filter((i) => {
    if (rows[i]._deleted) return false
    if (!step.row_filter) return true
    return evalCondition(step.row_filter, scopeFor({ ...frame, line_form: form, line_index: i }), true)
  })
}

export { liveRows, scopeOf }
export type { Row }
